/** Real-time notification system (Phase 51) §15 — a short, synthesized
 * two-tone chime via the Web Audio API rather than shipping an audio
 * asset. Deliberately best-effort: browsers block audio without a prior
 * user gesture on the page, and this fires from a background poll, so a
 * thrown/rejected play is swallowed silently — visual toasts remain the
 * reliable channel regardless (§15: "if sound is unreliable, prioritize
 * visual notifications"). One shared AudioContext, created lazily on
 * first use (constructing one before any user interaction can itself
 * throw in some browsers). */
let audioContext: AudioContext | null = null;

const getContext = (): AudioContext | null => {
  try {
    if (!audioContext) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      audioContext = new Ctor();
    }
    return audioContext;
  } catch {
    return null;
  }
};

export const playNotificationChime = () => {
  try {
    const ctx = getContext();
    if (!ctx) return;

    const playTone = (frequency: number, startOffset: number, duration: number) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0, ctx.currentTime + startOffset);
      gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + startOffset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startOffset + duration);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(ctx.currentTime + startOffset);
      oscillator.stop(ctx.currentTime + startOffset + duration + 0.05);
    };

    playTone(880, 0, 0.12);
    playTone(1108.73, 0.1, 0.16);
  } catch {
    // Autoplay restrictions or an unsupported browser — silently skip.
  }
};
